// client/src/pages/Blog.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const CATEGORIES = ['All', 'Clinical Research', 'Drug Development', 'Patient Advocacy', 'Regulatory', 'Public Health', 'Technology', 'News'];

const CATEGORY_COLORS = {
  'Clinical Research': '#dbeafe',
  'Drug Development': '#dcfce7',
  'Patient Advocacy': '#fce7f3',
  'Regulatory': '#fef9c3',
  'Public Health': '#e0f2fe',
  'Technology': '#ede9fe',
  'News': '#fee2e2',
};

const CATEGORY_TEXT = {
  'Clinical Research': '#1d4ed8',
  'Drug Development': '#15803d',
  'Patient Advocacy': '#be185d',
  'Regulatory': '#854d0e',
  'Public Health': '#0369a1',
  'Technology': '#7c3aed',
  'News': '#b91c1c',
};

function formatDate(d) {
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function Blog() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/blog/featured').then(({ data }) => setFeatured(data.blogs || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 9 };
    if (category !== 'All') params.category = category;
    axios.get('/api/blog', { params })
      .then(({ data }) => {
        setBlogs(data.blogs || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, category]);

  const handleCategory = (cat) => { setCategory(cat); setPage(1); };

  return (
    <div style={{ minHeight: 'calc(100vh - 56px)', background: 'var(--paper)' }}>
      {/* Hero */}
      <div style={{ background: 'var(--paper)', color: 'var(--ink)', padding: '52px 24px 44px', textAlign: 'center', borderBottom: '2px solid var(--ink)' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '3px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 14 }}>
          Research Insights
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 5vw, 52px)', lineHeight: 1.05, marginBottom: 12, color: 'var(--ink)' }}>
          The BioMedArticles <em>Journal</em>
        </h1>
        <p style={{ color: 'var(--muted)', fontSize: 15, maxWidth: 460, margin: '0 auto', lineHeight: 1.6 }}>
          Clinical research insights, regulatory updates, and trial discoveries for researchers, clinicians, and patients.
        </p>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 24px' }}>

        {/* Featured posts */}
        {featured.length > 0 && (
          <div style={{ marginBottom: 52 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '2.5px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 20 }}>Featured</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
              {featured.map(post => (
                <div
                  key={post._id}
                  onClick={() => navigate(`/blog/${post.slug}`)}
                  style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--ink)'; e.currentTarget.style.boxShadow = '6px 6px 0 rgba(10,10,10,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--line-card)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {post.image && (
                    <div style={{ height: 180, overflow: 'hidden', background: 'var(--paper-2)' }}>
                      <img src={post.image} alt={post.imageAlt || post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  )}
                  <div style={{ padding: '20px 22px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <span style={{ background: 'var(--paper-2)', color: 'var(--muted-2)', fontSize: 10, padding: '2px 8px', borderRadius: 0, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{post.category}</span>
                      <span style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)' }}>{post.readTime} min read</span>
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 400, lineHeight: 1.3, color: 'var(--ink)', marginBottom: 8 }}>{post.title}</div>
                    <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 12 }}>{post.excerpt}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{formatDate(post.publishedAt)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category filter */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 32 }}>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => handleCategory(cat)}
              style={{
                padding: '5px 14px', border: `1px solid ${category === cat ? 'var(--ink)' : 'var(--line)'}`,
                borderRadius: 0, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-mono)',
                letterSpacing: '1.5px', textTransform: 'uppercase',
                background: category === cat ? 'var(--ink)' : 'var(--card)', color: category === cat ? 'var(--paper)' : 'var(--muted)',
                transition: 'all 0.1s'
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Total count */}
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted-2)', marginBottom: 24, letterSpacing: '1.5px', textTransform: 'uppercase' }}>
          {loading ? 'Loading...' : `${total} article${total !== 1 ? 's' : ''}${category !== 'All' ? ` in ${category}` : ''}`}
        </div>

        {/* Blog grid */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <div className="spin" style={{ width: 28, height: 28, border: '2px solid var(--line-card)', borderTopColor: 'var(--ink)', borderRadius: '50%' }} />
          </div>
        ) : blogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--muted)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--ink)', marginBottom: 8 }}>No articles yet</div>
            <p>Check back soon for clinical research insights.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
            {blogs.map(post => (
              <article
                key={post._id}
                onClick={() => navigate(`/blog/${post.slug}`)}
                style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.15s', display: 'flex', flexDirection: 'column' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--ink)'; e.currentTarget.style.boxShadow = '6px 6px 0 rgba(10,10,10,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--line-card)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                {post.image && (
                  <div style={{ height: 160, overflow: 'hidden', background: 'var(--paper-2)', flexShrink: 0 }}>
                    <img src={post.image} alt={post.imageAlt || post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                <div style={{ padding: '18px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ background: 'var(--paper-2)', color: 'var(--muted-2)', fontSize: 10, padding: '2px 8px', borderRadius: 0, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{post.category}</span>
                    <span style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)' }}>{post.readTime} min</span>
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 19, fontWeight: 400, lineHeight: 1.3, color: 'var(--ink)', marginBottom: 8, flex: 1 }}>{post.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 14 }}>{post.excerpt}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--muted-2)', fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{formatDate(post.publishedAt)}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink)', fontWeight: 600, fontFamily: 'var(--font-mono)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Read →</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 40 }}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => (
              <button key={pg}
                style={{ width: 36, height: 36, border: `1px solid ${page === pg ? 'var(--ink)' : 'var(--line)'}`, background: page === pg ? 'var(--ink)' : 'var(--card)', color: page === pg ? 'var(--paper)' : 'var(--ink)', borderRadius: 0, fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}
                onClick={() => { setPage(pg); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              >{pg}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}